"use client";

import { c as _c } from "react/compiler-runtime";
import type { GeoPath, Selection, Timer } from "d3";
import type { GeoPermissibleObjects } from "d3-geo";
import type { Feature, FeatureCollection, Geometry, Polygon } from "geojson";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Iso3166_1 } from "@d0paminedriven/iso-3166-1";
import { interpolate as d3Interpolate, easeCubicInOut, geoArea, geoCentroid, geoDistance, geoGraticule10, geoInterpolate, geoOrthographic, geoPath, json, select, timer, transition } from "d3";
import { useInView } from "motion/react";
import { feature } from "topojson-client";
import type { CountryCodeToObjOutputStrictReturnProps, TopojsonShape as JSONDATA, World110m } from "@/types/topojson";
import { buildTerminatorGeoJSON } from "@/lib/terminator-polygon";
export function useWorldTour(t0) {
  const $ = _c(39);
  const {
    visitorData
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Iso3166_1();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const isoHelper = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = isoHelper.countryCodeToObjOutput("001");
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const earthDefault = t2 as CountryCodeToObjOutputStrictReturnProps;
  const MIN_SCALE = 136;
  const MAX_SCALE = 1360;
  const TINY_COUNTRY_SCALE = 680;
  const containerRef = useRef(null);
  const [isTourRunning, setIsTourRunning] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const dataRef = useRef(visitorData);
  const [currentCountry, setCurrentCountry] = useState(earthDefault);
  const [width, setWidth] = useState(600);
  const [height, setHeight] = useState(337.5);
  let t3;
  if ($[2] !== visitorData) {
    t3 = visitorData.reduce(_temp, 0);
    $[2] = visitorData;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [currentVisitors, setCurrentVisitors] = useState(t3);
  let t4;
  let t5;
  if ($[4] !== visitorData) {
    t4 = () => {
      setCurrentVisitors(visitorData.reduce(_temp2, 0));
    };
    t5 = [visitorData];
    $[4] = visitorData;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      once: false,
      amount: "some"
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const isInView = useInView(containerRef, t6);
  const gRef = useRef(null);
  const pathRef = useRef(null);
  let t7;
  let t8;
  if ($[8] !== visitorData) {
    t7 = () => {
      dataRef.current = visitorData;
    };
    t8 = [visitorData];
    $[8] = visitorData;
    $[9] = t7;
    $[10] = t8;
  } else {
    t7 = $[9];
    t8 = $[10];
  }
  useEffect(t7, t8);
  let t9;
  if ($[11] !== visitorData) {
    t9 = visitorData.toSorted(_temp3);
    $[11] = visitorData;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const sortedData = t9;
  const rotationTimerRef = useRef(null);
  let t10;
  if ($[13] !== height || $[14] !== width) {
    t10 = (bounds, currentScale, feature$0) => {
      const area = geoArea(feature$0);
      if (feature$0.id === "840" || feature$0.id === "156") {
        return 360;
      }
      const [t11, t12] = bounds;
      const [x0, y0] = t11;
      const [x1, y1] = t12;
      const dx = x1 - x0;
      const dy = y1 - y0;
      const boxSize = Math.sqrt(dx * dx + dy * dy);
      if (area < 0.001) {
        return Math.max(680, 850);
      }
      let scaleFactor;
      if (area < 0.005) {
        scaleFactor = 4 + (0.005 - area) / 0.005 * 2;
      } else {
        if (area < 0.03) {
          scaleFactor = 3;
        } else {
          if (area < 0.15) {
            scaleFactor = 2;
          } else {
            scaleFactor = 1.5;
          }
        }
      }
      let newScale = currentScale * Math.min(width, height) * scaleFactor / boxSize;
      newScale = Math.max(136, Math.min(1360, newScale));
      return newScale;
    };
    $[13] = height;
    $[14] = width;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  const computeScaleForBounds = t10;
  let t11;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = (country, duration) => {
      if (gRef.current && pathRef.current) {
        return new Promise(resolve => {
          const countryPath = gRef.current?.append("path").datum(country).attr("class", "highlight").attr("fill", "gold").attr("stroke", "none").attr("d", pathRef.current).style("pointer-events", "none");
          countryPath?.attr("opacity", 0).transition().duration(duration * 0.3).attr("opacity", 0.7).transition().duration(duration * 0.4).attr("opacity", 0.5).transition().duration(duration * 0.3).attr("opacity", 0).on("end", () => {
            countryPath.remove();
            resolve();
          });
        });
      }
    };
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const highlightCountry = t11;
  let t12;
  if ($[17] !== computeScaleForBounds || $[18] !== height || $[19] !== sortedData || $[20] !== visitorData || $[21] !== width) {
    t12 = worldData => {
      setIsTourRunning(true);
      setWidth(600);
      setHeight(window.innerWidth >= 640 ? width : width * 1.3333333333333333);
      const svg = select(containerRef.current).select("svg").attr("viewBox", `0 0 ${width} ${height}`).attr("preserveAspectRatio", "xMidYMid meet");
      svg.selectAll("*").remove();
      const countries = feature(worldData, worldData.objects.countries) as unknown as FeatureCollection<Geometry, JSONDATA>;
      const projection = geoOrthographic().scale(170).translate([width / 2, height / 2]);
      const path = geoPath(projection);
      const g = svg.append("g");
      svg.append("defs").append("radialGradient").attr("id", "globe-gradient").attr("cx", "50%").attr("cy", "50%").attr("r", "50%").selectAll("stop").data([{
        offset: "0%",
        color: "rgba(255,255,255,0.1)"
      }, {
        offset: "100%",
        color: "rgba(0,0,0,0)"
      }]).enter().append("stop").attr("offset", _temp4).attr("stop-color", _temp5);
      g.append("circle").attr("cx", width / 2).attr("cy", height / 2).attr("r", 170).attr("class", "globe-gradient").style("fill", "url(#globe-gradient)").style("transform-origin", "center");
      g.append("path").datum({
        type: "Sphere"
      } as GeoPermissibleObjects).attr("class", "sphere").attr("fill", "#222").attr("d", path);
      g.append("path").datum(geoGraticule10()).attr("class", "graticule").attr("fill", "none").attr("stroke", "#666").attr("stroke-width", 0.5).attr("d", path);
      g.selectAll("path.country").data(countries.features).enter().append("path").attr("class", "country").attr("fill", "#eee").attr("stroke", "#333").attr("d", path);
      const terminatorFeature = buildTerminatorGeoJSON(new Date(Date.now()));
      g.selectAll("path.terminator").data([terminatorFeature]).join("path").attr("class", "terminator").attr("fill", "rgba(0,0,0,0.3)").attr("stroke", "none").attr("d", path);
      gRef.current = g;
      pathRef.current = path;
      g.append("path").attr("class", "flight-path").attr("fill", "none").attr("stroke", "gold").attr("stroke-width", 2).attr("stroke-dasharray", "4 4").style("opacity", 0);
      const rotateAndZoomToCountry = async (feature_0, index, total) => {
        if (!feature_0) {
          return;
        }
        const [lon1, lat1] = geoCentroid(feature_0);
        const [currentLon, currentLat] = projection.rotate();
        const lon0 = -currentLon;
        const lat0 = -currentLat;
        const interpolateLonLat = geoInterpolate([lon0, lat0], [lon1, lat1]);
        const s0 = projection.scale();
        const s1 = computeScaleForBounds(path.bounds(feature_0), s0, feature_0);
        const area_0 = geoArea(feature_0);
        area_0 < 0.001 ? 0.9 : 0.7;
        const targetScale = Math.max(153, Math.min(255, s1));
        const distance = geoDistance([lon0, lat0], [lon1, lat1]);
        const duration_0 = 2000 + distance * 3000;
        await new Promise(resolve_0 => {
          transition().duration(duration_0).ease(easeCubicInOut).tween("rotate", () => t => {
            const [lonI, latI] = interpolateLonLat(t);
            const scale = d3Interpolate(s0, targetScale)(t);
            projection.rotate([-lonI, -latI, 23.44]).scale(scale);
            if (gRef.current && pathRef.current) {
              gRef.current.selectAll("path").attr("d", d_1 => pathRef.current != null ? pathRef.current(d_1 as GeoPermissibleObjects ?? "") : "");
              const newTerminatorFeature = buildTerminatorGeoJSON(new Date(Date.now()));
              gRef.current.selectAll("path.terminator").data([newTerminatorFeature]).join("path").attr("class", "terminator").attr("fill", "rgba(0,0,0,0.3)").attr("stroke", "none").attr("d", pathRef.current);
              gRef.current.select(".atmosphere-glow").attr("r", scale * 1.01);
            }
          }).on("end", () => resolve_0());
        });
        await highlightCountry(feature_0, 1000);
      };
      const runTour = async () => {
        setIsTourRunning(true);
        const totalCountries = sortedData.length;
        const tourPath = g.append("path").attr("class", "tour-path").attr("fill", "none").attr("stroke", "gold").attr("stroke-width", 2).attr("opacity", 0.5);
        const tourCoordinates = sortedData.map(t13 => {
          const [countryCode] = t13;
          const feature_1 = countries.features.find(f => f.id === countryCode);
          return feature_1 ? geoCentroid(feature_1) : null;
        }).filter(Boolean);
        tourPath.attr("d", path({
          type: "LineString",
          coordinates: tourCoordinates
        } as GeoPermissibleObjects));
        for (let i = 0; i < sortedData.length; i++) {
          const [countryCode_0, visitors] = sortedData[i];
          const countryInfo = isoHelper.countryCodeToObjOutput(countryCode_0) as CountryCodeToObjOutputStrictReturnProps;
          if (countryInfo) {
            setCurrentCountry(countryInfo);
            setCurrentVisitors(visitors);
            const feature_2 = countries.features.find(f_0 => f_0.id === countryCode_0);
            if (!feature_2) {
              continue;
            }
            await rotateAndZoomToCountry(feature_2, i, totalCountries);
            const segmentPath = g.append("path").attr("class", "current-segment").attr("fill", "none").attr("stroke", "gold").attr("stroke-width", 3).attr("opacity", 1);
            if (i < totalCountries - 1) {
              const nextCountry = sortedData[i + 1][0];
              const nextFeature = countries.features.find(f_1 => f_1.id === nextCountry);
              if (nextFeature) {
                const currentCoords = geoCentroid(feature_2);
                const nextCoords = geoCentroid(nextFeature);
                segmentPath.attr("d", path({
                  type: "LineString",
                  coordinates: [currentCoords, nextCoords]
                } as GeoPermissibleObjects));
              }
            }
            await new Promise(_temp6);
            segmentPath.remove();
          }
        }
        setCurrentCountry(earthDefault);
        setCurrentVisitors(visitorData.reduce(_temp7, 0));
        await new Promise(resolve_2 => {
          transition().duration(2000).ease(easeCubicInOut).tween("rotate", () => {
            const currentRotation = projection.rotate() as [number, number, number];
            const currentScale_0 = projection.scale();
            return t_0 => {
              const rotation = currentRotation.map(d_2 => d_2 * (1 - t_0));
              rotation[2] = 23.44;
              const scale_0 = d3Interpolate(currentScale_0, 170)(t_0);
              projection.rotate(rotation as [number, number, number]).scale(scale_0);
              g.selectAll("path").attr("d", d_3 => path(d_3 as GeoPermissibleObjects) ?? "");
            };
          }).on("end", () => {
            tourPath.remove();
            resolve_2();
          });
        });
        setIsTourRunning(false);
      };
      runTour().catch(err => {
        console.error(err);
        setIsTourRunning(false);
      });
    };
    $[17] = computeScaleForBounds;
    $[18] = height;
    $[19] = sortedData;
    $[20] = visitorData;
    $[21] = width;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const createVisualization = t12;
  let t13;
  let t14;
  if ($[23] !== height || $[24] !== isTourRunning || $[25] !== width) {
    t13 = () => {
      if (!isTourRunning && containerRef.current) {
        const svg_0 = select(containerRef.current).select("svg").attr("viewBox", `0 0 ${width} ${height}`).attr("preserveAspectRatio", "xMidYMid meet");
        const g_0 = svg_0.select("g");
        g_0.select(".globe-gradient").attr("cx", width / 2).attr("cy", height / 2);
        const projection_0 = geoOrthographic().scale(170).translate([width / 2, height / 2]);
        const path_0 = geoPath(projection_0);
        if (!rotationTimerRef.current) {
          rotationTimerRef.current = timer(elapsed => {
            const rotation_0 = [elapsed * 0.01 % 360, -20, 23.44] as [number, number, number];
            projection_0.rotate(rotation_0);
            g_0.selectAll("path").attr("d", d_4 => path_0(d_4 as GeoPermissibleObjects) ?? "");
          });
        }
        return () => {
          if (rotationTimerRef.current) {
            rotationTimerRef.current.stop();
            rotationTimerRef.current = null;
          }
        };
      }
    };
    t14 = [isTourRunning, height, width];
    $[23] = height;
    $[24] = isTourRunning;
    $[25] = width;
    $[26] = t13;
    $[27] = t14;
  } else {
    t13 = $[26];
    t14 = $[27];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[28] !== isTourRunning) {
    t15 = () => {
      if (!isTourRunning) {
        const updateTerminator = () => {
          if (gRef.current && pathRef.current) {
            const newTerminatorFeature_0 = buildTerminatorGeoJSON(new Date(Date.now()));
            gRef.current.selectAll("path.terminator").data([newTerminatorFeature_0]).join("path").attr("class", "terminator").attr("fill", "rgba(0,0,0,0.3)").attr("stroke", "none").attr("d", pathRef.current);
          }
        };
        updateTerminator();
        const intervalId = setInterval(() => {
          updateTerminator();
        }, 60000);
        return () => clearInterval(intervalId);
      }
    };
    t16 = [isTourRunning];
    $[28] = isTourRunning;
    $[29] = t15;
    $[30] = t16;
  } else {
    t15 = $[29];
    t16 = $[30];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[31] !== createVisualization || $[32] !== hasPlayed || $[33] !== isInView) {
    t17 = () => {
      if (isInView && !hasPlayed) {
        setHasPlayed(true);
        json("/topojson/countries-110m.json").then(worldData_0 => {
          if (worldData_0) {
            createVisualization(worldData_0);
          }
        }).catch(console.error);
      }
    };
    t18 = [isInView, hasPlayed, createVisualization];
    $[31] = createVisualization;
    $[32] = hasPlayed;
    $[33] = isInView;
    $[34] = t17;
    $[35] = t18;
  } else {
    t17 = $[34];
    t18 = $[35];
  }
  useEffect(t17, t18);
  let t19;
  if ($[36] !== currentCountry || $[37] !== currentVisitors) {
    t19 = {
      containerRef,
      currentCountry,
      currentVisitors
    };
    $[36] = currentCountry;
    $[37] = currentVisitors;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  return t19;
}

/*

      // Add atmosphere glow gradient
      const atmosphereGradient = svg
        .append("defs")
        .append("radialGradient")
        .attr("id", "atmosphereGlow")
        .attr("cx", "50%")
        .attr("cy", "50%")
        .attr("r", "50%")
        .attr("gradientUnits", "userSpaceOnUse")
        .attr(
          "gradientTransform",
          `translate(${width / 2},${height / 2}) scale(${DEFAULT_SCALE * 2})`
        );
      atmosphereGradient
        .append("stop")
        .attr("offset", "90%")
        .attr("stop-color", "#4B71FF")
        .attr("stop-opacity", 0.05);
      atmosphereGradient
        .append("stop")
        .attr("offset", "100%")
        .attr("stop-color", "#4B71FF")
        .attr("stop-opacity", 0);

      // Add atmosphere glow
      g.append("circle")
        .attr("cx", width / 2)
        .attr("cy", height / 2)
        .attr("r", DEFAULT_SCALE * 1.01)
        .attr("fill", "url(#atmosphereGlow)")
        .attr("class", "atmosphere-glow");

  const rotateAndZoomToCountry = useCallback(async (
    feature: Feature<Geometry, JSONDATA>,
    index: number,
    total: number,
    projection: GeoProjection,
    g: Selection<SVGGElement, unknown, null, undefined>,
    path: GeoPath<any, GeoPermissibleObjects>
  ) => {
    if (!feature) return;

    const [lon1, lat1] = geoCentroid(feature);
    const [currentLon, currentLat] = projection.rotate();
    const lon0 = -currentLon;
    const lat0 = -currentLat;

    const interpolateLonLat = geoInterpolate([lon0, lat0], [lon1, lat1]);

    const s0 = projection.scale();
    const s1 = computeScaleForBounds(path.bounds(feature), s0, feature);

    // Adjust the intermediate scale based on country size
    const area = geoArea(feature);
    const TINY_COUNTRY_THRESHOLD = 0.001;
    const intermediateScaleFactor =
      area < TINY_COUNTRY_THRESHOLD ? 0.9 : 0.7;
    const _s2 = s0 * intermediateScaleFactor; // Less extreme zoom-out for small countries

    // Use a more consistent scale range
    const minScale = DEFAULT_SCALE * 0.9;
    const maxScale = DEFAULT_SCALE * 1.5;
    const targetScale = Math.max(minScale, Math.min(maxScale, s1));

    // Calculate the progress of the entire tour
    const _progress = index / total;

    // Adjust duration based on distance, but keep it more consistent
    const distance = geoDistance([lon0, lat0], [lon1, lat1]);
    const duration = 2000 + distance * 3000; // Base duration plus distance-based addition

    await new Promise<void>(resolve => {
      transition()
        .duration(duration)
        .ease(easeCubicInOut) // Use a smoother easing function
        .tween("rotate", () => {
          return (t: number) => {
            const [lonI, latI] = interpolateLonLat(t);
            const scale = d3Interpolate(s0, targetScale)(t);
            projection.rotate([-lonI, -latI, AXIAL_TILT]).scale(scale);
            g.selectAll("path").attr(
              "d",
              d => path(d as GeoPermissibleObjects) ?? ""
            );
          };
        })
        .on("end", () => resolve());
    });

    await highlightCountry(feature, 1000, g, path);
  }, [computeScaleForBounds, highlightCountry])

*/
function _temp7(sum_1, t0) {
  const [, count_1] = t0;
  return sum_1 + count_1;
}
function _temp6(resolve_1) {
  return setTimeout(resolve_1, 1000);
}
function _temp5(d_0) {
  return d_0.color;
}
function _temp4(d) {
  return d.offset;
}
function _temp3(a, b) {
  return b[1] - a[1];
}
function _temp2(sum_0, t0) {
  const [, count_0] = t0;
  return sum_0 + count_0;
}
function _temp(sum, t0) {
  const [, count] = t0;
  return sum + count;
}